import { makeObservable } from 'mobx';

import RootStore from 'stores/Root';

import { FormActionType, FormBuilderType } from 'utils/api/modules/entities/entities.types';

import { ProtectorType } from 'core/constants';

import FormBuilder from '../FormBuilder';

class FormBuilderValidation {
  store: RootStore;

  constructor(
    rootStore: RootStore,
    private formBuilder: FormBuilder
  ) {
    this.store = rootStore;
    makeObservable(this);
  }

  validateSectionUniqueness(): boolean {
    const sectionTitles = this.formBuilder.containers.map(
      (container) => this.formBuilder.sectionLogic.getSectionTitleAndDescription(container).title
    );
    const uniqueSectionTitles = new Set(sectionTitles);
    if (sectionTitles.length !== uniqueSectionTitles.size) {
      this.store.notification.enqueueErrorSnackbar('Section names must be unique');
      return false;
    }
    return true;
  }

  validateEmptyForm(): boolean {
    if (this.formBuilder.data?.order.length === 0) {
      this.store.notification.enqueueErrorSnackbar('Form must contain at least one section');
      return false;
    }
    return true;
  }

  validateEmptySections(): boolean {
    const emptySections = this.formBuilder.containers.filter(
      (container) => this.formBuilder.items[container].length === 0
    );
    if (emptySections.length > 0) {
      this.store.notification.enqueueErrorSnackbar('Sections cannot be empty');
      return false;
    }
    return true;
  }

  validateFormName(): boolean {
    if (this.formBuilder.formName === '') {
      this.store.notification.enqueueErrorSnackbar('Form name cannot be empty');
      return false;
    }
    return true;
  }

  validateQuestionsUniqueness(transformed: FormBuilderType): boolean {
    const allKeys: string[] = [];
    Object.values(transformed.properties).forEach((section) => {
      allKeys.push(...section.order);
    });

    const uniqueKeys = new Set(allKeys);
    if (allKeys.length !== uniqueKeys.size) {
      this.store.notification.enqueueErrorSnackbar('Question keys must be unique');
      return false;
    }
    return true;
  }

  validateLookupRecordTypes({ order, properties }: FormBuilderType): boolean {
    for (const sectionKey of order) {
      const section = properties[sectionKey];
      const typeless = section?.order
        .map((questionKey) => section.properties[questionKey])
        .find(
          (question) =>
            question?.questionType === ProtectorType.Lookup &&
            !(question.properties as unknown as { lookupType?: string } | undefined)?.lookupType
        );
      if (typeless) {
        this.store.notification.enqueueErrorSnackbar(
          `“${typeless.description || section.title}” needs a record type in Related to`
        );
        return false;
      }
    }
    return true;
  }

  validateActions({ order, properties }: FormBuilderType): boolean {
    const actionError = (action: FormActionType, sectionKey: string, sectionIndex: number): string | undefined => {
      const nextSection = 'nextSection' in action.then ? action.then.nextSection : undefined;
      const submit = 'submit' in action.then && action.then.submit;
      if (!nextSection && !submit) return 'has a rule with nothing chosen for it to do';
      if (nextSection && submit) return 'has a rule with more than one outcome';
      if (nextSection && order.indexOf(nextSection) <= sectionIndex) {
        return 'has a rule that goes to a missing or earlier section';
      }

      if (action.when === true) return undefined;
      if (typeof action.when !== 'object' || !action.when.equals) return 'has a rule with no condition';
      if (!properties[sectionKey].order.includes(action.when.equals[0])) {
        return 'has a rule on a question that is no longer in that section';
      }
      return undefined;
    };

    for (const [sectionIndex, sectionKey] of order.entries()) {
      const error = (properties[sectionKey]?.actions ?? [])
        .map((action) => actionError(action, sectionKey, sectionIndex))
        .find(Boolean);
      if (error) {
        this.store.notification.enqueueErrorSnackbar(`“${properties[sectionKey].title}” ${error}`);
        return false;
      }
    }
    return true;
  }
}

export default FormBuilderValidation;
